import { Alert, Button, Space, Tabs, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLockFn, useEventEmitter, useEventListener, useRequest } from 'ahooks';
import { getManualList } from '@/api/sip/bookmap';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import TabContent from './components/TabContent';
import SearchAll from './components/SearchAll';
import type { SelectData } from './components/CatalogTree';
import type { ManualListResponse } from '@/api/sip/bookmap/list';
import { useAutoHeight } from 'xui-pro';
import Empty from './components/Empty';
import api from '@/api';
import { nanoid } from 'nanoid';
import { useTranslation } from '@/hooks/useTranslation';
import { BOOK_MAP_TYPE } from './constants';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import WorkingHourDetail from './components/WorkingHourDetail';
import NoPermission from './components/NoPermisson';
import styles from './index.module.less';

const src = `/staticApp/sip/pdf-viewer.html`;
const PDFViewer = ({ fileId }) => {
  const [iframeName] = useState<string>(`pdf-viewer-${nanoid()}`);
  const $ = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { _height } = useAutoHeight(containerRef, true, undefined, 395, false, 12);
  const [showAlertMsg, setShowAlertMsg] = useState<{ message: string; reason: string }>();
  const {
    loading,
    error,
    data: signUrl
  } = useRequest(
    async () => {
      try {
        const res = await api.common.listByFileIdsUsingPost({
          splicingFileIds: fileId
        });
        if (res.code !== 200) {
          throw new Error(res.msg);
        }
        if (res.data.length === 0 || !res.data[0].filePath) {
          throw new Error($('sign 返回数据为空'));
        }
        const url = res.data[0].filePath;
        return url;
      } catch (e: any) {
        message.error(e.message || $('pdf文件加载失败'));
        throw new Error(e.message || $('pdf文件加载失败'));
      }
      // return 'https://xiaolong.deploy-test.xiaopeng.com/ossMicroApp/oss-xdragon-subsystem-sip/P7%20User%20Manual.pdf';
      // return 'https://xiaolong.deploy-test.xiaopeng.com/ossMicroApp/oss-xdragon-subsystem-sip/P7%20Global%20Edition_%20Service%20Manual-origine原版大文件.pdf';
    },
    { manual: false }
  );

  const openUrl = useLockFn(async () => {
    const res = await api.common.listByFileIdsUsingPost({
      splicingFileIds: fileId
    });
    if (res.code !== 200) {
      message.error(res.msg);
      return;
    }
    if (res.data.length === 0 || !res.data[0].filePath) {
      message.error('sign 返回数据为空');
    }
    const url = res.data[0].filePath;
    window.open(url);
  });

  useEventListener('message', e => {
    if (e.source !== window?.[iframeName]) {
      return;
    }
    if (e.data?.type === 'documenterror') {
      setShowAlertMsg(e.data.result);
    }
  });
  return (
    <div ref={containerRef} style={{ height: _height }}>
      {_height ? (
        loading || error ? (
          <div style={{ width: '100%', height: _height }}>
            <Empty description={error?.message || $('pdf文件加载中')} />
          </div>
        ) : (
          <>
            {showAlertMsg && (
              <Alert
                message={showAlertMsg.message}
                description={showAlertMsg.reason}
                type='warning'
                action={
                  <Button onClick={openUrl} type='link'>
                    {$('点击使用浏览器默认插件打开')}
                  </Button>
                }
              />
            )}
            <iframe
              src={`${src}?file=${encodeURIComponent(signUrl!)}`}
              width='100%'
              height={_height}
              style={{ border: 'none' }}
              name={iframeName}
            />
          </>
        )
      ) : (
        <></>
      )}
    </div>
  );
};

const DetailToolbar = () => {
  const $ = useTranslation();
  const { modelCode, vin } = useParams<{ modelCode: string; vin: string }>();
  const region = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );
  const lang = useSelector<RootState, string>(state => state.LocaleModel.serverLang, shallowEqual);
  const isGlobal = useSelector<RootState>(state => state.LocaleModel.isGlobal, shallowEqual);

  const [activeKey, setActiveKey] = useState<string>();
  const tabChange$ = useEventEmitter<void>();

  const [defaultSelected, setDefaultSelected] = useState<SelectData | undefined>();
  const searchSelect$ = useEventEmitter<SelectData>();
  searchSelect$.useSubscription((data: SelectData) => {
    setDefaultSelected(data);
    setActiveKey(data.bookMapCode);
  });
  const [data, setData] = useState<ManualListResponse>();
  const containerRef = useRef<HTMLDivElement>(null);
  const { _height } = useAutoHeight(containerRef, true, undefined, 395, false, 12);

  const { data: manualList } = useRequest(
    async () => {
      try {
        const res = await getManualList({
          vin,
          modelCode: modelCode,
          lang,
          region
        });
        if (res.code !== 200) {
          message.error(res.msg);
          return [];
        }
        setData(res.data);

        const match = window.location.pathname.match(/(\/afs-sip\/.+)/);
        const path = match ? match[1] : '';
        if (path) {
          if (vin) {
            window.xDragonBridge.updateTab({
              path,
              title: `SIP${`-${res.data.seriesCodeName ?? ''} ${
                res.data.modelCodeName ?? ''
              }-${vin.substring(vin.length - 6)}`}`
            });
          } else {
            window.xDragonBridge.updateTab({
              path,
              title: `SIP${`-${res.data.seriesCodeName ?? ''} ${res.data.modelCodeName ?? ''}`}`
            });
          }
        }

        return res.data.bookmapItemList;
      } catch (error: any) {
        message.error(error.message);
        return [];
      }
    },
    { manual: false }
  );

  const onChange = useCallback(
    (newActiveKey: string) => {
      tabChange$.emit();
      setActiveKey(newActiveKey);
    },
    [tabChange$]
  );

  useEffect(() => {
    if (!manualList || manualList.length === 0) {
      return;
    }
    setActiveKey(manualList?.[0].bookmapCode);
  }, [manualList]);

  if (!manualList || manualList.length === 0) {
    return <></>;
  }
  return (
    <div ref={containerRef} style={{ height: _height }} className={styles.detail}>
      <Tabs defaultActiveKey={manualList[0].bookmapCode} onChange={onChange} activeKey={activeKey}>
        {manualList.map(item => (
          <Tabs.TabPane tab={$(BOOK_MAP_TYPE[item.bookmapType])} key={item.bookmapCode}>
            {item.auth ? (
              item.fileId ? (
                <PDFViewer fileId={item.fileId} />
              ) : (
                <TabContent
                  record={item}
                  modelCodeName={data?.modelCodeName}
                  seriesCodeName={data?.seriesCodeName}
                  defaultSelected={defaultSelected}
                  searchSelect$={searchSelect$}
                  active={activeKey === item.bookmapCode}
                  tabChange$={tabChange$}
                />
              )
            ) : (
              <NoPermission />
            )}
          </Tabs.TabPane>
        ))}
        <Tabs.TabPane tab={$('工时手册')} key={'workingHour'}>
          {data?.auth ? <WorkingHourDetail isGlobal={isGlobal} /> : <NoPermission />}
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <Space size={4}>
              <SearchOutlined style={{ marginRight: 0 }} />
              {$('全文搜索')}
            </Space>
          }
          key={'globalSearch'}
        >
          {data?.auth ? (
            <SearchAll searchSelect$={searchSelect$} manuaList={manualList} />
          ) : (
            <NoPermission />
          )}
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default DetailToolbar;
